import React, { useEffect, useState, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";
import { SERVICE_LOCATION, GET_UNSUBMITTED_WORKSHEETS } from "./../constants/endpoints";

import "./../assets/styles/table.css";
import "./../assets/styles/fitter.css";
import "./../assets/styles/reports.css";

const UnsubmittedWorksheetsReport = (props) => {
    const toast = useRef(null);

    const [unsubmittedWorksheets, setUnsubmittedWorksheets] = useState(null);
    const [filteredUnsubmittedWorksheets, setFilteredUnsubmittedWorksheets] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [minDate, setMinDate] = useState(null);
    const [showUnsubmittedData, setShowUnsubmittedData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [disableSubmitButton, setDisableSubmitButton] = useState(true);

    const { data } = props;
    const userDepot = (data !== undefined && data !== null) ? data.data.userDetails.depot : "";

    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    useEffect(() => {
        initFilters();
    }, []);

    const getUnsubmittedWorksheets = () => {
        setLoading(true);
        const startDate = getFormattedDate(selectedStartDate);
        const endDate = getFormattedDate(selectedEndDate);
        const requestedData = {
            StartDate: startDate,
            EndDate: endDate,
            Depot: userDepot
        }

        const endPoint = SERVICE_LOCATION + GET_UNSUBMITTED_WORKSHEETS;
        axios.post(endPoint, requestedData, {
            headers: {
                Authorization: `Bearer ${data.data.token}`,
            },
        })
            .then((response) => {
                setLoading(false);
                if (response.data.isSuccess) {
                    const processedData = response.data.data.getProcessedUnsubmittedWorksheetsBetweenDates;
                    setUnsubmittedWorksheets(processedData);
                    setFilteredUnsubmittedWorksheets(processedData);
                }
                else if (!response.data.isSuccess) {
                    setUnsubmittedWorksheets(null);
                    setFilteredUnsubmittedWorksheets(null);
                    toast.current.show({ severity: "error", summary: "Error", detail: response.data.errorMessage, life: 3000 });
                }
            })
            .catch((error) => {
                toast.current.show({ severity: "error", summary: "Error", detail: error, life: 3000 });
            })
    };

    const getFormattedDate = (dateToFormat) => {
        const year = dateToFormat.getFullYear();
        const month = String(dateToFormat.getMonth() + 1).padStart(2, "0");
        const day = String(dateToFormat.getDate()).padStart(2, "0");
        const formatedDate = `${year}-${month}-${day}`;
        return formatedDate;
    }

    const handleStartDateChange = (e) => {
        const dateSelected = e.value;
        setSelectedStartDate(dateSelected);
        setMinDate(dateSelected);
        setSelectedEndDate(null);
        setDisableSubmitButton(true);
    }

    const handleEndDateChange = (e) => {
        const dateSelected = e.value;
        setSelectedEndDate(dateSelected);
        setDisableSubmitButton(false);
    }

    const downloadUnsubmittedWorksheets = () => {
        getUnsubmittedWorksheets();
        setShowUnsubmittedData(true);
    }

    //export functionality
    const saveAsExcelFile = (buffer, fileName) => {
        import("file-saver").then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
                let EXCEL_EXTENSION = ".xlsx";
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });

                module.default.saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const exportExcel = () => {
        import("xlsx").then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(filteredUnsubmittedWorksheets);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
            const excelBuffer = xlsx.write(workbook, {
                bookType: "xlsx",
                type: "array"
            });

            saveAsExcelFile(excelBuffer, "unsubmittedWorksheets");
        });
    };
    //end export functionality

    // Search functionality
    const getFilteredUnsubmittedWorksheets = (filteredData) => {
        setFilteredUnsubmittedWorksheets(filteredData);
    }

    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            clientName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            siteName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            siteAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
            siteTelNo: { value: null, matchMode: FilterMatchMode.CONTAINS },
            plotNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
            plotDescription: { value: null, matchMode: FilterMatchMode.CONTAINS },
            worksheetNo: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measureCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measureDescription: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measureType: { value: null, matchMode: FilterMatchMode.CONTAINS },
            planDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
            depot: { value: null, matchMode: FilterMatchMode.CONTAINS },
            fitterName: { value: null, matchMode: FilterMatchMode.CONTAINS }
        })
        setGlobalFilterValue("");
    }

    const renderHeader = () => {
        return (
            <div className="row">
                <div className="col-12 col-sm-3 col-md-3 col-lg-3 col-xl-3">
                    <div className="flex align-items-center justify-content-end gap-2">
                        <Button className="mar-r-10" type="button" icon="pi pi-file-excel" severity="success" onClick={exportExcel} rounded tooltip="Export Report" />
                    </div>
                </div>
                <div className="col-12 col-sm-9 col-md-9 col-lg-9 col-xl-9">
                    <div className="flex justify-content-end txt-right">
                        <Button className="mar-r-10" type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} tooltip="clear filters" tooltipOptions={{ position: "bottom" }} />
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                        </span>
                    </div>
                </div>

            </div>
        );
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters["global"].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const header = renderHeader();
    // Search functionality Ends here    

    return (
        <div className={`app ${loading ? "disabled" : ""}`}>
            {
                loading &&
                <div className="spinner-overlay">
                    <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                </div>
            }

            <Toast ref={toast} />
            <div className="worksheets-section padl-20">
                <h4>Unsubmitted Worksheets</h4>
                <div className="row pad-tb-10">
                    <div className="col-3">
                        <label htmlFor="start-date">Start Date</label>
                        <Calendar id="start-date" name="start-date" value={selectedStartDate} onChange={handleStartDateChange} showIcon dateFormat="dd/mm/yy" />
                    </div>
                    <div className="col-3">
                        <label htmlFor="end-date">End Date</label>
                        <Calendar id="end-date" name="end-date" value={selectedEndDate} minDate={minDate} onChange={handleEndDateChange} showIcon dateFormat="dd/mm/yy" />
                    </div>
                    <div className="col-3">
                        <Button id="submit" label="Submit" disabled={disableSubmitButton} onClick={downloadUnsubmittedWorksheets} />
                    </div>
                </div>

                {
                    showUnsubmittedData &&
                    <div className="row">
                        <div className="col-12">
                            <DataTable className="font-12" value={unsubmittedWorksheets} dataKey="id" paginator rows={10}
                                rowsPerPageOptions={[10, 25, 50]} filters={filters} header={header} filterDisplay="row" onValueChange={getFilteredUnsubmittedWorksheets}
                                globalFilterFields={["id", "clientName", "siteName", "siteAddress", "siteTelNo", "plotNumber",
                                    "plotDescription", "worksheetNo", "worksheetCode", "measureCode", "measureDescription",
                                    "measureType", "planDate", "depot", "fitterName"]}
                                scrollable tableStyle={{ minWidth: "100rem" }}>
                                <Column field="id" header="Id" className="table-header" hidden></Column>
                                <Column field="clientName" key="clientName" header="Client Name" filter filterPlaceholder="Search by client name" className="table-header" ></Column>
                                <Column field="siteName" header="Site Name" filter filterPlaceholder="Search by site name" className="table-header"></Column>
                                <Column field="siteAddress" header="Site Address" filter filterPlaceholder="Search by site address" className="table-header"></Column>
                                <Column field="siteTelNo" header="Site Telephone No" filter filterPlaceholder="Search by telephone" className="table-header"></Column>
                                <Column field="plotNumber" header="Plot Number" filter filterPlaceholder="Search by plot number" className="table-header"></Column>
                                <Column field="plotDescription" header="Plot Description" filter filterPlaceholder="Search by plot description" className="table-header"></Column>
                                <Column field="worksheetNo" header="Worksheet No" filter filterPlaceholder="Search by worksheet no" className="table-header"></Column>
                                <Column field="worksheetCode" header="Worksheet Code" filter filterPlaceholder="Search by worksheet code" className="table-header" hidden></Column>
                                <Column field="measureCode" header="Measure Code" filter filterPlaceholder="Search by measure code" className="table-header"></Column>
                                <Column field="measureDescription" header="Measure Description" filter filterPlaceholder="Search by measure description" className="table-header"></Column>
                                <Column field="measureType" header="Measure Type" filter filterPlaceholder="Search by measure type" className="table-header"></Column>
                                <Column field="planDate" header="Planed Date" filter filterPlaceholder="Search by plan date" className="table-header"></Column>
                                <Column field="depot" header="Depot" filter filterPlaceholder="Search by depot" className="table-header"></Column>
                                <Column field="fitterName" header="FitterName" filter filterPlaceholder="Search by fitter name" className="table-header"></Column>
                            </DataTable>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default UnsubmittedWorksheetsReport;